import { GetServerSideProps } from 'next'
import { useEffect } from 'react'
import { useRouter } from 'next/router'

import AccountPageContextProvider from 'account/components/AccountPageContextProvider'
import filterCardsWithAddressByCount from 'server/utils/productTemplate/filterCardsWithAddressByCount'
import { Address } from 'shared/types/Address'
import { BillingInfo } from 'shared/types/BillingInfo'
import { MyAccount } from 'account/components/MyAccount'
import { getLocalePath, serverSideConfigs } from 'localization'
import { parseCookies } from 'shared/utils/server-utils'
import { serverRequestService } from 'server/services/serverRequestService'
import { useAuth } from 'shared/hooks/useAuth'
import { useUser } from 'shared/hooks/useUser'

type MyAccountPageProps = {
  activeOrderNumber: number
  billingAddress: Address
  creditCards: BillingInfo[]
  shippingAddress: Address
}

const MyAccountPage = ({
  activeOrderNumber,
  billingAddress,
  creditCards,
  shippingAddress,
}: MyAccountPageProps) => {
  const router = useRouter()
  const { logoutUser } = useAuth()
  const { currentUser, isAuthenticated } = useUser()

  useEffect(() => {
    if (!isAuthenticated) {
      // this is a hacky way to ensure user is redirected to Login if not logged in
      router.replace(
        '/customer/account/login?redirectTo=/my-account',
        '/customer/account/login?redirectTo=/my-account',
        { locale: router.locale },
      )
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (currentUser?.isGuest) {
      const logout = async () => await logoutUser()
      logout()
      router.replace(
        '/customer/account/login?redirectTo=/my-account',
        '/customer/account/login?redirectTo=/my-account',
        { locale: router.locale },
      )
    }
  }, [currentUser])

  if (currentUser && !currentUser.isGuest) {
    return (
      <AccountPageContextProvider orderNumber={activeOrderNumber}>
        <MyAccount
          shippingAddress={shippingAddress}
          billingAddress={billingAddress}
          creditCards={creditCards}
        />
      </AccountPageContextProvider>
    )
  }
  return null
}

export const getServerSideProps: GetServerSideProps = async ({ req, res, locale }) => {
  const { jwt } = parseCookies(req)
  const activeOrder: number = parseInt(req?.query['active-order'], 10)
  if (!jwt) {
    return {
      redirect: {
        destination: getLocalePath('/customer/account/login?redirectTo=/my-account', locale),
      },
    }
  }

  try {
    const { data: addressData } = await serverRequestService(req, res, 'sneakers').get(
      '/api/v1/addresses/',
    )
    const billingAddress =
      addressData?.addresses?.find(address => address?.addressType === 'billing') || null
    const shippingAddress =
      addressData?.addresses?.find(address => address?.addressType === 'shipping') || null

    const { data: billingInfosData } = await serverRequestService(req, res, 'sneakers').get(
      '/api/v1/billing_infos/',
    )
    const creditCards = filterCardsWithAddressByCount(1)(billingInfosData?.billingInfos)

    return {
      props: {
        ...(await serverSideConfigs(res, locale)),
        activeOrderNumber: activeOrder || null,
        billingAddress,
        creditCards,
        shippingAddress,
      },
    }
  } catch (error) {
    return {
      props: {
        ...(await serverSideConfigs(res, locale)),
        error: error?.message,
      },
    }
  }
}

export default MyAccountPage
